import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions } from "../spec";

const SHEET = "BR Cotizador";

// SKU, Producto, Categoría, Precio
const PRODUCTOS: [string, string, string, number][] = [
  ["EL-01", "Laptop 14 pulgadas", "Electrónica", 18999],
  ["EL-02", "Monitor 27 pulgadas", "Electrónica", 6499],
  ["EL-03", "Impresora multifuncional", "Electrónica", 4299],
  ["EL-04", "Proyector Full HD", "Electrónica", 9899],
  ["EL-05", "Teléfono IP", "Electrónica", 1899],
  ["MU-01", "Escritorio ejecutivo", "Muebles", 7499],
  ["MU-02", "Silla ergonómica", "Muebles", 4599],
  ["MU-03", "Archivero metálico", "Muebles", 3299],
  ["MU-04", "Mesa de juntas", "Muebles", 12999],
  ["MU-05", "Librero", "Muebles", 2799],
  ["PA-01", "Papel bond (caja)", "Papelería", 489],
  ["PA-02", "Carpetas (paquete)", "Papelería", 159],
  ["PA-03", "Plumas (caja)", "Papelería", 119],
  ["PA-04", "Notas adhesivas", "Papelería", 69],
  ["PA-05", "Engrapadora", "Papelería", 229],
];
const CATEGORIAS = ["Electrónica", "Muebles", "Papelería"] as const;
// Tipo cliente, Electrónica, Muebles, Papelería
const DESCUENTOS: [string, number, number, number][] = [
  ["Mayorista", 0.12, 0.15, 0.2],
  ["Distribuidor", 0.08, 0.1, 0.12],
  ["Minorista", 0.03, 0.05, 0.05],
];
// Cliente, Tipo, Estado
const CLIENTES: [string, string, string][] = [
  ["Grupo Altamira", "Mayorista", "Nuevo León"],
  ["Comercial Norte", "Distribuidor", "Coahuila"],
  ["Farmacias Vida", "Minorista", "Jalisco"],
  ["Hotel Mirador", "Minorista", "Quintana Roo"],
  ["Constructora Roble", "Distribuidor", "Puebla"],
  ["Clínica San José", "Mayorista", "Yucatán"],
];
// Estado, Costo — Yucatán is missing on purpose.
const ENVIO: [string, number][] = [
  ["Nuevo León", 250],
  ["Coahuila", 300],
  ["Jalisco", 350],
  ["Puebla", 300],
];
// Línea, SKU, Cantidad
const COTIZACION: [number, string, number][] = [
  [1, "EL-01", 3],
  [2, "MU-02", 3],
  [3, "PA-01", 20],
  [4, "EL-02", 3],
  [5, "MU-04", 1],
  [6, "PA-03", 10],
  [7, "EL-05", 5],
  [8, "MU-03", 2],
];

const IN = { cliente: "Clínica San José", envioEstandar: 450 };
const prod = (sku: string) => PRODUCTOS.find((p) => p[0] === sku)!;
const cliente = CLIENTES.find((c) => c[0] === IN.cliente)!;
const tipo = cliente[1];
const descuento = (sku: string) =>
  DESCUENTOS.find((d) => d[0] === tipo)![1 + CATEGORIAS.indexOf(prod(sku)[2] as (typeof CATEGORIAS)[number])];
const envio = ENVIO.find((e) => e[0] === cliente[2])?.[1] ?? IN.envioEstandar;
const masCaro = [...COTIZACION].sort((a, b) => prod(b[1])[3] - prod(a[1])[3])[0];

export const spec: FuncionProjectSpec = {
  projectId: "funciones-cotizador",
  sheets: [
    {
      name: SHEET,
      title: "Cotizador",
      subtitle: "Arma la cotización de un cliente — cada columna amarilla pide un tipo de búsqueda distinto",
      tables: [
        { name: "Productos", anchor: "A4", headers: ["SKU", "Producto", "Categoría", "Precio"], rows: PRODUCTOS, formats: { Precio: "$#,##0" } },
        {
          name: "Descuentos",
          anchor: "F4",
          headers: ["Tipo cliente", ...CATEGORIAS],
          rows: DESCUENTOS,
          formats: { Electrónica: "0%", Muebles: "0%", Papelería: "0%" },
        },
        { name: "Clientes", anchor: "F10", headers: ["Cliente", "Tipo", "Estado"], rows: CLIENTES },
        { name: "Envio", anchor: "F19", headers: ["Estado", "Costo"], rows: ENVIO, formats: { Costo: "$#,##0" } },
        {
          name: "Cotizacion",
          anchor: "K4",
          headers: ["Línea", "SKU", "Cantidad"],
          rows: COTIZACION,
          answerColumns: ["Producto", "Precio", "Descuento"],
          formats: { Precio: "$#,##0", Descuento: "0%" },
        },
      ],
      inputs: [
        { address: "S4", label: "Cliente", value: IN.cliente },
        { address: "S5", label: "Envío estándar", value: IN.envioEstandar, format: "$#,##0" },
      ],
      answers: [
        { address: "S8", label: "Tarea 1 · Tipo de cliente" },
        { address: "S9", label: "Tarea 5 · Costo de envío", format: "$#,##0" },
        { address: "S10", label: "Tarea 6 · Producto más caro" },
      ],
    },
  ],
  checks: {
    1: { kind: "cell", sheet: SHEET, address: "S8", fns: ["XLOOKUP"], expected: tipo },
    2: {
      kind: "column", sheet: SHEET, table: "Cotizacion", column: "Producto", fns: ["VLOOKUP"],
      expected: COTIZACION.map((c) => prod(c[1])[1]),
    },
    3: {
      kind: "column", sheet: SHEET, table: "Cotizacion", column: "Precio", fns: ["XLOOKUP"],
      expected: COTIZACION.map((c) => prod(c[1])[3]),
    },
    4: {
      kind: "column", sheet: SHEET, table: "Cotizacion", column: "Descuento", fns: ["INDEX", "MATCH", "XLOOKUP"],
      minCounts: { MATCH: 2 },
      expected: COTIZACION.map((c) => descuento(c[1])),
    },
    5: {
      kind: "cell", sheet: SHEET, address: "S9", fns: ["IFNA", "XLOOKUP"], minCounts: { XLOOKUP: 2 },
      expected: envio,
    },
    6: { kind: "cell", sheet: SHEET, address: "S10", fns: ["INDEX", "MATCH", "MAX"], expected: prod(masCaro[1])[1] },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;
const DESC_FORMULA =
  "=INDICE(Descuentos[[Electrónica]:[Papelería]],COINCIDIR($S$8,Descuentos[Tipo cliente],0),COINCIDIR(BUSCARX([@SKU],Productos[SKU],Productos[Categoría]),Descuentos[[#Encabezados],[Electrónica]:[Papelería]],0))";

export const project: ProjectDefinition = {
  id: "funciones-cotizador",
  title: "Reto final: Cotizador",
  subtitle: "BUSCARV, BUSCARX, INDICE y COINCIDIR en un solo archivo",
  description:
    "Ventas te pide un cotizador: eliges un cliente y la hoja completa sola el tipo de cliente, cada producto con su precio, el descuento que le toca por categoría, el costo de envío a su estado y cuál es el producto más caro.\n\nCada paso pide una búsqueda distinta. Parte del reto es elegir la herramienta correcta.",
  topics: [
    "Búsquedas que dependen de otras búsquedas",
    "Descuento por tipo de cliente × categoría",
    "BUSCARX anidado con SI.ND",
    "INDICE + COINCIDIR + MAX",
  ],
  tasks: [
    {
      id: 1,
      level: "intermedio",
      title: "Tarea 1: Tipo de cliente",
      description:
        "En la hoja <strong>BR Cotizador</strong>, celda <strong>S8</strong>: devuelve el <strong>Tipo</strong> del cliente de <strong>S4</strong> buscándolo en la tabla <strong>Clientes</strong> con <strong>BUSCARX</strong>.",
      solution: [`Selecciona ${F("S8")}.`, `Escribe ${F("=BUSCARX(S4,Clientes[Cliente],Clientes[Tipo])")}.`],
      expectedResult: "Mayorista",
      finalFormula: "=BUSCARX(S4,Clientes[Cliente],Clientes[Tipo])",
    },
    {
      id: 2,
      level: "intermedio",
      title: "Tarea 2: Producto de cada línea",
      description:
        "En la tabla <strong>Cotizacion</strong>, columna <strong>Producto</strong>: trae el nombre de cada SKU con <strong>BUSCARV</strong>.",
      solution: [`Selecciona ${F("N5")}.`, `Escribe ${F("=BUSCARV([@SKU],Productos,2,FALSO)")}.`],
      expectedResult: "Nombre de cada producto cotizado",
      finalFormula: "=BUSCARV([@SKU],Productos,2,FALSO)",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Precio de cada línea",
      description: "En la columna <strong>Precio</strong>: trae el precio de cada SKU, ahora con <strong>BUSCARX</strong>.",
      solution: [`Selecciona ${F("O5")}.`, `Escribe ${F("=BUSCARX([@SKU],Productos[SKU],Productos[Precio])")}.`],
      expectedResult: "Precio de lista de cada producto",
      finalFormula: "=BUSCARX([@SKU],Productos[SKU],Productos[Precio])",
    },
    {
      id: 4,
      level: "reto",
      title: "Tarea 4: Descuento por línea",
      description: conditions(
        "En la columna <strong>Descuento</strong>: el porcentaje de la tabla <strong>Descuentos</strong> (F4) que le toca a cada línea:",
        [
          "<strong>Fila</strong> → el tipo de cliente de la Tarea 1 (<strong>$S$8</strong>)",
          "<strong>Columna</strong> → la <strong>categoría</strong> del producto, que hay que buscar primero con BUSCARX",
          "<strong>INDICE</strong> con dos <strong>COINCIDIR</strong> hace el cruce",
        ]
      ),
      solution: [
        `Selecciona ${F("P5")}.`,
        `La categoría de la línea es ${F("BUSCARX([@SKU],Productos[SKU],Productos[Categoría])")}; ese resultado es lo que buscas en los encabezados de Descuentos.`,
        `Escribe ${F(DESC_FORMULA)}.`,
      ],
      expectedResult: "12%, 15% o 20% para un Mayorista",
      finalFormula: DESC_FORMULA,
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Costo de envío",
      description: conditions(
        "En <strong>S9</strong>: el costo de envío al estado del cliente:",
        [
          "Busca el <strong>Estado</strong> del cliente de S4 en <strong>Clientes</strong>",
          "Con ese estado, busca el <strong>Costo</strong> en la tabla <strong>Envio</strong> (F19)",
          "Si el estado no está en la tabla, usa el <strong>Envío estándar</strong> de <strong>S5</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("S9")}.`,
        "El BUSCARX de adentro trae el estado; el de afuera busca ese estado en Envio. Si no lo encuentra da #N/D, y SI.ND pone el envío estándar.",
        `Escribe ${F("=SI.ND(BUSCARX(BUSCARX(S4,Clientes[Cliente],Clientes[Estado]),Envio[Estado],Envio[Costo]),S5)")}.`,
      ],
      expectedResult: "$450 (Yucatán no está en la tabla de envíos)",
      finalFormula: "=SI.ND(BUSCARX(BUSCARX(S4,Clientes[Cliente],Clientes[Estado]),Envio[Estado],Envio[Costo]),S5)",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Producto más caro de la cotización",
      description:
        "En <strong>S10</strong>: devuelve el <strong>nombre</strong> del producto con el <strong>precio más alto</strong> de la cotización. Combina <strong>MAX</strong>, <strong>COINCIDIR</strong> e <strong>INDICE</strong>.",
      solution: [
        `Selecciona ${F("S10")}.`,
        "MAX da el precio más alto; COINCIDIR encuentra en qué línea está; INDICE trae el nombre de esa línea.",
        `Escribe ${F("=INDICE(Cotizacion[Producto],COINCIDIR(MAX(Cotizacion[Precio]),Cotizacion[Precio],0))")}.`,
      ],
      expectedResult: "Laptop 14 pulgadas",
      finalFormula: "=INDICE(Cotizacion[Producto],COINCIDIR(MAX(Cotizacion[Precio]),Cotizacion[Precio],0))",
    },
  ],
};
